import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, X, Plus, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { fmt } from "@/lib/site";
import { poster, PRODUCT_REELS } from "@/lib/media";

// Lightbox con UN solo <video> montado a la vez. Los reels del listado son
// posters <img>: cero elementos de video en el DOM hasta que el usuario toca.
const ReelLightbox = ({ item, product, onClose }) => {
    const { addItem } = useCart();
    const [added, setAdded] = useState(false);

    useEffect(() => {
        const esc = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", esc);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", esc);
            document.body.style.overflow = "";
        };
    }, [onClose]);

    const add = () => {
        if (!product) return;
        addItem(product, 1);
        toast.success("Agregado al carrito", { description: product.nombre });
        setAdded(true);
        setTimeout(() => setAdded(false), 1200);
    };

    return (
        <div
            data-testid="bestseller-lightbox"
            className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div className="relative h-[78vh] aspect-[9/16] max-w-[92vw]" onClick={(e) => e.stopPropagation()}>
                <video
                    src={item.src}
                    poster={poster(item.key)}
                    autoPlay
                    controls
                    playsInline
                    className="w-full h-full object-contain rounded-2xl bg-black shadow-lift"
                />
                <button
                    onClick={onClose}
                    aria-label="Cerrar video"
                    data-testid="bestseller-lightbox-close"
                    className="absolute -top-3 -right-3 h-10 w-10 rounded-full bg-white text-ink flex items-center justify-center shadow-lift hover:bg-crema transition-colors"
                >
                    <X size={20} />
                </button>
                {/* Card de compra: precio VIVO desde la ficha (editable en admin) */}
                <div className="mt-3 bg-white rounded-2xl shadow-lift px-4 py-3 flex items-center justify-between gap-3" data-testid="bestseller-buy-card">
                    <div className="min-w-0">
                        <p className="font-display font-semibold text-ink text-sm leading-tight truncate">{product?.nombre || item.label}</p>
                        {product ? (
                            <p className="font-display text-xl font-bold text-verde leading-tight">
                                {fmt(product.precio_usd)} <span className="text-[11px] font-sans font-semibold text-mut">+ IVA</span>
                                <span className="text-[11px] text-madera-dark font-medium font-sans ml-2">Mayoreo {fmt(product.precio_mayoreo)}</span>
                            </p>
                        ) : (
                            <p className="text-xs text-mut">Disponible en tienda</p>
                        )}
                    </div>
                    {product && (
                        <div className="flex items-center gap-2 shrink-0">
                            <Link
                                to={`/producto/${product.id}`}
                                className="hidden sm:inline-flex items-center gap-1.5 text-verde font-semibold text-xs hover:text-naranja transition-colors"
                            >
                                Ver ficha <ArrowRight size={13} />
                            </Link>
                            <button
                                onClick={add}
                                data-testid="bestseller-add-cart"
                                className={`h-11 w-11 rounded-full flex items-center justify-center text-white transition-colors ${added ? "bg-verde" : "bg-naranja hover:bg-naranja-dark"}`}
                                aria-label="Agregar al carrito"
                            >
                                {added ? <Check size={20} /> : <Plus size={20} strokeWidth={2.4} />}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const PosterCard = ({ item, product, onOpen }) => (
    <button
        type="button"
        data-testid={`bestseller-${item.key}`}
        onClick={() => onOpen(item)}
        className="group relative shrink-0 w-[58%] sm:w-[36%] md:w-[24%] lg:w-[18%] aspect-[9/16] rounded-2xl overflow-hidden bg-verde-900 shadow-soft text-left"
    >
        <img
            src={poster(item.key)}
            alt={item.label}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-3 left-3 bg-naranja text-white text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-bold">
            Best Seller
        </span>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
            <span className="h-14 w-14 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play size={22} className="text-verde ml-1" fill="currentColor" />
            </span>
        </div>
        <div className="absolute bottom-4 left-4 right-4">
            <p className="text-white font-display font-semibold text-sm leading-tight">{item.label}</p>
            {product && (
                <p className="text-madera font-display font-bold text-base mt-0.5" data-testid={`bestseller-price-${item.key}`}>
                    {fmt(product.precio_usd)}
                </p>
            )}
        </div>
    </button>
);

export const BestSellersReels = () => {
    const [open, setOpen] = useState(null);
    const [bySku, setBySku] = useState({});

    useEffect(() => {
        api.get("/products", { params: { etiqueta: "BEST SELLER", limit: 100 } })
            .then((r) => {
                const map = {};
                (r.data.items || []).forEach((p) => { map[p.sku] = p; });
                setBySku(map);
            })
            .catch(() => {});
    }, []);

    return (
        <>
            <div className="overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="bestsellers-strip">
                <div className="flex gap-4">
                    {PRODUCT_REELS.map((item) => (
                        <PosterCard key={item.key} item={item} product={bySku[item.sku]} onOpen={setOpen} />
                    ))}
                </div>
            </div>
            {open && <ReelLightbox item={open} product={bySku[open.sku]} onClose={() => setOpen(null)} />}
        </>
    );
};
